import { PencilSimple } from "@phosphor-icons/react";
import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import styled from "@emotion/styled";
import { Badge, type BadgeProps } from "@/components/primitives/Badge";
import { Button, IconButton, type ButtonProps } from "@/components/primitives/Button";

export type EntityDisplayCardMetadata = {
  label: ReactNode;
  value: ReactNode;
};

export type EntityDisplayCardAction = {
  id: string;
  label: ReactNode;
  ariaLabel?: string;
  icon?: ReactNode;
  disabled?: boolean;
  variant?: ButtonProps["variant"];
};

export type EntityDisplayCardProps = Omit<HTMLAttributes<HTMLDivElement>, "title"> & {
  title: ReactNode;
  description?: ReactNode;
  status?: ReactNode;
  statusTone?: BadgeProps["tone"];
  metadata?: EntityDisplayCardMetadata[];
  actions?: EntityDisplayCardAction[];
  onAction?: (action: EntityDisplayCardAction) => void;
};

/* Exact compiled output of the pre-migration Tailwind utilities. */

const EntityCardRoot = styled("div", {
  label: "AgenticEntityDisplayCard-root"
})({
  borderRadius: "var(--bfrs-radius-xl)",
  borderWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  paddingLeft: "1rem",
  paddingRight: "1rem",
  paddingTop: ".75rem",
  paddingBottom: ".75rem"
});

const EntityCardHeader = styled("div", {
  label: "AgenticEntityDisplayCard-header"
})({
  display: "flex",
  alignItems: "flex-start",
  gap: ".75rem"
});

const EntityCardHeaderText = styled("div", {
  label: "AgenticEntityDisplayCard-headerText"
})({
  minWidth: "0px",
  flex: "1 1 0%"
});

const EntityCardTitleRow = styled("div", {
  label: "AgenticEntityDisplayCard-titleRow"
})({
  display: "flex",
  minWidth: "0px",
  flexWrap: "wrap",
  alignItems: "center",
  gap: ".5rem"
});

const EntityCardTitle = styled("span", {
  label: "AgenticEntityDisplayCard-title"
})({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: ".875rem",
  lineHeight: "1.25rem",
  fontWeight: 600,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
});

const EntityCardDescription = styled("div", {
  label: "AgenticEntityDisplayCard-description"
})({
  marginTop: ".25rem",
  fontSize: ".75rem",
  lineHeight: "1.25rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))"
});

const EntityCardActions = styled("div", {
  label: "AgenticEntityDisplayCard-actions"
})({
  display: "flex",
  flexShrink: 0,
  alignItems: "center",
  gap: ".25rem"
});

const EntityCardMetadata = styled("dl", {
  label: "AgenticEntityDisplayCard-metadata"
})({
  marginTop: ".75rem",
  display: "grid",
  gap: ".5rem",
  "@media (min-width:768px)": {
    gridTemplateColumns: "repeat(2,minmax(0,1fr))"
  }
});

const EntityCardMetaItem = styled("div", {
  label: "AgenticEntityDisplayCard-metaItem"
})({
  minWidth: "0px"
});

const EntityCardMetaLabel = styled("dt", {
  label: "AgenticEntityDisplayCard-metaLabel"
})({
  fontSize: "11px",
  fontWeight: 600,
  textTransform: "uppercase",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const EntityCardMetaValue = styled("dd", {
  label: "AgenticEntityDisplayCard-metaValue"
})({
  marginTop: ".125rem",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: ".75rem",
  lineHeight: "1rem",
  fontWeight: 500,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
});

export const EntityDisplayCard = forwardRef<HTMLDivElement, EntityDisplayCardProps>(({ className, title, description, status, statusTone = "success", metadata = [], actions = [], onAction, ...props }, ref) => (
  <EntityCardRoot ref={ref} className={className} {...props}>
    <EntityCardHeader>
      <EntityCardHeaderText>
        <EntityCardTitleRow>
          <EntityCardTitle>{title}</EntityCardTitle>
          {status && <Badge tone={statusTone}>{status}</Badge>}
        </EntityCardTitleRow>
        {description && <EntityCardDescription>{description}</EntityCardDescription>}
      </EntityCardHeaderText>
      {actions.length > 0 && (
        <EntityCardActions>
          {actions.map((action) =>
            action.icon ? (
              <IconButton key={action.id} icon={action.icon} aria-label={action.ariaLabel ?? String(action.label)} variant={action.variant ?? "ghost"} size="sm" disabled={action.disabled} onClick={() => onAction?.(action)} />
            ) : (
              <Button key={action.id} variant={action.variant ?? "ghost"} size="sm" disabled={action.disabled} leftIcon={<PencilSimple size={13} />} onClick={() => onAction?.(action)}>
                {action.label}
              </Button>
            )
          )}
        </EntityCardActions>
      )}
    </EntityCardHeader>
    {metadata.length > 0 && (
      <EntityCardMetadata>
        {metadata.map((item, index) => (
          <EntityCardMetaItem key={index}>
            <EntityCardMetaLabel>{item.label}</EntityCardMetaLabel>
            <EntityCardMetaValue>{item.value}</EntityCardMetaValue>
          </EntityCardMetaItem>
        ))}
      </EntityCardMetadata>
    )}
  </EntityCardRoot>
));

EntityDisplayCard.displayName = "EntityDisplayCard";
